/**
 * 该逻辑抽离于react-i18next的同款组件: Trans;
 * 由于react-i18next的Trans组件必须搭配其i18next-context-provider，非常重，而且项目中已经有了多语言的解决方案了，所以将它里面的字符串处理组件抽离出来，
 * 相关使用文档：https://react.i18next.com/legacy-v9/trans-component，
 */
import { Fragment, isValidElement, cloneElement, createElement, Children } from 'react';
import HTML from 'html-parse-stringify';

const isObject = (obj) => typeof obj === 'object' && obj !== null;
const isString = (obj) => typeof obj === 'string';

// 分割符
const dividers = {
  left: '{',
  right: '}',
};

// 替换多语言中的占位符
export function replaceValueFromTemplate(str, values) {
  let res = str;
  const keys = Object.keys(values || {});
  // 替换内容
  keys.forEach((key) => {
    const regEpx = new RegExp(`${dividers.left}\\s*${key}\\s*?${dividers.right}`, 'g');
    res = res.replace(regEpx, `${values[key]}`);
  });

  return res;
}

const hasChildren = (node, checkLength) => {
  if (!node) return false;
  const base = node.props?.children ?? node.children;
  if (checkLength) return base.length > 0;
  return !!base;
};

const getChildren = (node) => {
  if (!node) return [];
  const children = node.props?.children ?? node.children;
  return node.props?.i18nIsDynamicList ? getAsArray(children) : children;
};

const hasValidReactChildren = (children) => Array.isArray(children) && children.every(isValidElement);

const getAsArray = (data) => (Array.isArray(data) ? data : [data]);

const mergeProps = (source, target) => {
  const newTarget = {
    ...target,
  };
  newTarget.props = Object.assign(source.props, target.props);
  return newTarget;
};

const renderNodes = (children, targetString) => {
  if (targetString === '') return [];
  const keepArray = [];
  const emptyChildrenButNeedsHandling =
    targetString && new RegExp(keepArray.map((keep) => `<${keep}`).join('|')).test(targetString);
  if (!children && !emptyChildrenButNeedsHandling) return [targetString];
  const getData = (childs) => {
    const childrenArray = getAsArray(childs);
    childrenArray.forEach((child) => {
      if (isString(child)) return;
      if (hasChildren(child)) getData(getChildren(child));
      else if (isObject(child) && !isValidElement(child)) Object.assign({}, child);
    });
  };
  getData(children);
  const ast = HTML.parse(`<0>${targetString}</0>`);
  const renderInner = (child, node, rootReactNode) => {
    const childs = getChildren(child);
    const mappedChildren = mapAST(childs, node.children, rootReactNode);
    return (hasValidReactChildren(childs) && mappedChildren.length === 0) || child.props?.i18nIsDynamicList
      ? childs
      : mappedChildren;
  };
  const pushTranslatedJSX = (child, inner, mem, i, isVoid) => {
    if (child.dummy) {
      child.children = inner;
      mem.push(
        cloneElement(
          child,
          {
            key: i,
          },
          isVoid ? undefined : inner
        )
      );
    } else {
      mem.push(
        ...Children.map([child], (c) => {
          const props = {
            ...c.props,
          };
          delete props.i18nIsDynamicList;
          return createElement(
            c.type,
            {
              ...props,
              key: i,
              ref: c.ref,
            },
            isVoid ? null : inner
          );
        })
      );
    }
  };
  const mapAST = (reactNode, astNode, rootReactNode) => {
    const reactNodes = getAsArray(reactNode);
    const astNodes = getAsArray(astNode);
    return astNodes.reduce((mem, node, i) => {
      const translationContent = node.children?.[0]?.content;
      if (node.type === 'tag') {
        let tmp = reactNodes[parseInt(node.name, 10)];
        if (rootReactNode.length === 1 && !tmp) tmp = rootReactNode[0][node.name];
        if (!tmp) tmp = {};
        const child =
          Object.keys(node.attrs).length !== 0
            ? mergeProps(
                {
                  props: node.attrs,
                },
                tmp
              )
            : tmp;
        const isElement = isValidElement(child);
        const isValidTranslationWithChildren = isElement && hasChildren(node, true) && !node.voidElement;
        const isEmptyTransWithHTML = emptyChildrenButNeedsHandling && isObject(child) && child.dummy && !isElement;
        const isKnownComponent = isObject(children) && Object.hasOwnProperty.call(children, node.name);
        if (isString(child)) {
          const value = child;
          mem.push(value);
        } else if (hasChildren(child) || isValidTranslationWithChildren) {
          const inner = renderInner(child, node, rootReactNode);
          pushTranslatedJSX(child, inner, mem, i);
        } else if (isEmptyTransWithHTML) {
          const inner = mapAST(reactNodes, node.children, rootReactNode);
          pushTranslatedJSX(child, inner, mem, i);
        } else if (Number.isNaN(parseFloat(node.name))) {
          if (isKnownComponent) {
            const inner = renderInner(child, node, rootReactNode);
            pushTranslatedJSX(child, inner, mem, i, node.voidElement);
          } else if (keepArray.indexOf(node.name) > -1) {
            if (node.voidElement) {
              mem.push(
                createElement(node.name, {
                  key: `${node.name}-${i}`,
                })
              );
            } else {
              const inner = mapAST(reactNodes, node.children, rootReactNode);
              mem.push(
                createElement(
                  node.name,
                  {
                    key: `${node.name}-${i}`,
                  },
                  inner
                )
              );
            }
          } else if (node.voidElement) {
            mem.push(`<${node.name} />`);
          } else {
            const inner = mapAST(reactNodes, node.children, rootReactNode);
            mem.push(`<${node.name}>${inner}</${node.name}>`);
          }
        } else if (isObject(child) && !isElement) {
          const content = node.children[0] ? translationContent : null;
          if (content) mem.push(content);
        } else {
          pushTranslatedJSX(child, translationContent, mem, i, node.children.length !== 1 || !translationContent);
        }
      } else if (node.type === 'text') {
        const content = node.content;
        mem.push(content);
      }
      return mem;
    }, []);
  };
  const result = mapAST(
    [
      {
        dummy: true,
        children: children || [],
      },
    ],
    ast,
    getAsArray(children || [])
  );
  return getChildren(result[0]);
};

/**
 *
 * @param {Object} props
 * @param {string} props.text 多语言文案
 * @param {Object} [props.components] 多语言文案中需要替换的html标签
 * @param {Object} [props.values] 多语言文案中需要动态替换的内容
 * @returns {ReactNode}
 */

export function Trans({ text, components, values }) {
  let translation = text;
  // 替换占位符
  if (isObject(values)) {
    translation = replaceValueFromTemplate(text, values);
  }
  if (components) {
    Object.keys(components).forEach((c) => {
      const comp = components[c];
      if (
        typeof comp.type === 'function' ||
        !comp.props ||
        !comp.props.children ||
        (translation.indexOf(`${c}/>`) < 0 && translation.indexOf(`${c} />`) < 0)
      )
        return;
      function Componentized() {
        return createElement(Fragment, null, comp);
      }
      components[c] = createElement(Componentized);
    });
  }
  const content = renderNodes(components, translation);
  return content;
}
